import * as z from 'zod'

// Type for Zod refinement context with form data
export type FormContext = {
  data?: {
    start_time?: string
  }
}

// Define the form schema using Zod
export const eventFormSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  location: z.string().optional(),
  address: z.string().optional(),
  bgg_game_id: z.string().optional(), // Store BGG game ID separately
  start_time: z.string().superRefine((val: string, ctx) => {
    if (isNaN(Date.parse(val))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid start date and time',
      })
    }
  }),
  end_time: z
    .string()
    .superRefine((val: string, ctx) => {
      if (isNaN(Date.parse(val))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please enter a valid end date and time',
        })
      }
    })
    .superRefine((val: string, ctx: z.RefinementCtx & FormContext) => {
      if (!ctx.data?.start_time) return
      const start = new Date(ctx.data.start_time)
      const end = new Date(val)
      if (!(end > start)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End time must be after start time',
        })
      }
    }),
  min_players: z.coerce.number().int().min(1, 'Minimum 1 player required'),
  max_players: z.coerce.number().int().min(1).optional(),
  skill_level: z.enum(['beginner', 'intermediate', 'advanced', 'all_levels'] as const),
  is_private: z.boolean().default(false),
  game_id: z.string().optional(),
  meetup_id: z.string().optional(),
  is_recurring: z.boolean().default(false),
  recurrence_pattern: z
    .enum(['daily', 'weekly', 'biweekly', 'monthly', 'custom'] as const)
    .optional(),
  recurrence_end_date: z.string().optional(),
  recurrence_count: z.coerce.number().int().min(1).optional(),
  im_playing: z.boolean().default(true), // New field for auto-RSVP
  game_data: z.string().optional(),
  color: z.string().optional().default('#15803d'), // Event color field
})

export type EventFormValues = z.infer<typeof eventFormSchema>
